import styled, { css } from 'styled-components';
import { device } from '../../styles/devices';
import { COLOR_BASE_GREY_5, SPACING_L_M, SPACING_M_L } from '@audi/audi-ui-design-tokens';

interface HeaderProps {
  backgroundColor: string;
  darkenedBackgroundColor: string;
  fallback: boolean;
  color: string;
  nonAudi: boolean;
}

interface HeaderPropsNonAudi {
  backgroundColor: string;
  darkenedBackgroundColor: string;
  fallback: boolean;
  color: string;
  nonAudi: boolean;
  mediaImages: string[];
}

interface BackgroundProps {
  image: string;
  nonAudi?: boolean;
  fallback: boolean;
  hasDealerImages?: boolean;
}

interface HeaderTextContainerProps {
  nonAudi: boolean;
  isUsed: boolean;
  fallback: boolean;
  hasDealerImages?: boolean;
}

interface VehicleHeaderWrapperProps {
  isUsed?: boolean;
  fallback?: boolean;
}

export const Header = styled.header<HeaderProps>`
  position: relative;
  font-family: 'Audi Type';
  position: relative;
  background: ${(props) =>
    `linear-gradient(180deg, ${props.darkenedBackgroundColor} 24.45%, ${props.backgroundColor} 63.3%, white 63.4%)`};
  min-height: ${(props) => (props.fallback ? `450px` : '350px')};
  color: ${(props) => props.color};
  overflow: hidden;
  @media ${device.tablet} {
    background: ${(props) =>
      `linear-gradient(to right, ${props.backgroundColor} 100%, white 100%)`};
  }
  @media ${device.laptopM} {
    background: ${(props) => `linear-gradient(to right, ${props.backgroundColor} 60%, white 60%)`};
  }
`;

export const HeaderUsed = styled.header<HeaderPropsNonAudi>`
  position: relative;
  background: ${(props) => `linear-gradient(180deg, ${props.backgroundColor} 63.3%, white 63.4%)`};
  color: ${({ color }) => color};
  font-family: 'Audi Type';
  min-height: 520px;
  @media ${device.tablet} {
    min-height: 312px;
    background: ${(props) => `linear-gradient(to right, ${props.backgroundColor} 100%, white 0%)`};
    margin-bottom: 0;
    display: flex;
  }
  @media ${device.laptopM} {
    min-height: 503px;
    background: ${(props) => `linear-gradient(to right, ${props.backgroundColor} 60%, white 60%)`};
    width: calc(100% - 164px);
  }
  @media ${device.laptopL} {
    width: calc(100% - 100px - 74px);
  }
`;

export const HeadingContainer = styled.div`
  min-height: 323px;
  padding-top: 42px;
  position: relative;
  @media ${device.tablet} {
    min-height: 392px;
    padding: 60px 0px 0px 60px;
  }
  @media ${device.laptopL} {
    width: 60%;
    min-height: 312px;
    padding: 60px 0px 0px 0px;
    ${(props: { fallback?: boolean | undefined; backgroundColor?: string | undefined }) => {
      if (props.fallback)
        return css`
          position: relative;
          background: ${props.backgroundColor};
          width: auto;
          left: -100px;
        `;
      return css``;
    }}
  }
  @media ${device.laptopM} {
    width: 50%;
    min-height: 503px;
    position: unset;
    ${(props: { fallback?: boolean | undefined; backgroundColor?: string | undefined }) => {
      if (props.fallback)
        return css`
          position: relative;
          background: ${props.backgroundColor};
          width: auto;
        `;
      return css``;
    }}
  }
  @media ${device.desktop} {
    ${(props: { fallback?: boolean | undefined; backgroundColor?: string | undefined }) => {
      if (props.fallback)
        return css`
          left: 0px;
        `;
      return css``;
    }}
  }
`;
export const TabletSubHeader = styled.div`
  width: 100%;
  background-color: ${COLOR_BASE_GREY_5};
  position: relative;
  min-height: 427.21px;
  @media ${device.laptop} {
    min-height: 516px;
  }
`;

export const HeadingContainerNonAudi = styled.div`
  padding-top: 42px;
  @media ${device.tablet} {
    display: flex;
    position: relative;
    background: ${({ backgroundColor }: { backgroundColor: string }) => backgroundColor};
    min-height: 312px;
    width: 95%;
    padding: 60px;
  }
  @media ${device.laptopM} {
    min-height: 503px;
  }
  @media ${device.laptopL} {
    padding: 42px 0 40px;
  }
  @media ${device.desktop} {
    left: 88px;
  }
`;

export const HeaderTextContainer = styled.div<HeaderTextContainerProps>`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding-bottom: ${({ hasDealerImages }) => hasDealerImages && '36px'};
  @media ${device.tablet} {
    justify-content: space-between;
    padding: 0;
    width: ${({ nonAudi, isUsed }: { nonAudi?: boolean; isUsed?: boolean }) =>
      nonAudi || isUsed ? '100%' : '80%'};
  }
  @media ${device.laptopM} {
    width: ${({ nonAudi, isUsed }) => (nonAudi || isUsed ? '30%' : '60%')};
    ${(props: { fallback?: boolean | undefined }) => {
      if (props.fallback)
        return css`
          width: 37%;
        `;
      return css``;
    }}
  }
  @media ${device.laptopL} {
    width: ${({ nonAudi, isUsed }) => (nonAudi || isUsed ? '33%' : '60%')};
  }
  @media ${device.laptopXL} {
    width: ${({ nonAudi, isUsed }) => (nonAudi || isUsed ? '39%' : 'auto')};
  }
`;

export const BackgroundImage = styled.div<BackgroundProps>`
  background-image: ${(props) => `url(${props.image})`};
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  position: absolute;
  right: 0;
  left: 0;
  padding-bottom: ${(props) => (props.nonAudi ? '75%' : '60%')};
  ${(props: { fallback?: boolean | undefined }) => {
    if (props.fallback)
      return css`
        padding-bottom: 0px;
      `;
    return css``;
  }}
  @media ${device.tablet} {
    right: 0;
    min-height: 503px;
    top: 0;
    height: 0;
    background-size: contain;
  }
  @media ${device.laptopM} {
    width: ${({ hasDealerImages }) => (hasDealerImages ? '64%' : '67%')};
    background-position: center;
    padding-bottom: 33%;
    right: 0;
    top: 0;
    left: auto;
    position: absolute;
    background-size: cover;
    height: 100%;
    ${(props: { fallback?: boolean | undefined }) => {
      if (props.fallback)
        return css`
          padding-bottom: 0px;
        `;
      return css``;
    }};
  }
  @media ${device.laptopL} {
    width: 100%;
    background-position: right;
    background-size: contain;
  }
`;

export const ImageMobileUsed = styled.img`
  width: 100vw;
  margin-left: -28px;
`;

export const FallbackImage = styled(ImageMobileUsed)``;

export const Image = styled.img`
  position: absolute;
  top: calc(273px - (281.25px / 2));
  right: calc(50% - (395px / 2));
  width: 375px;
  height: auto;
  transform: scale(1.2);
  margin-top: calc(173px - (281.25px / 2));

  @media (min-width: 376px) {
    top: calc(250px - (281.25px / 2));
    right: calc(50% - (395px / 2));
    transform: scale(1.4);
  }
  @media ${device.mobileL} {
    top: calc(250px - (281.25px / 2));
    right: calc(50% - (395px / 2));
    transform: scale(1.5);
  }
  @media (min-width: 600px) {
    margin-top: 0;
  }
  @media ${device.tablet} {
    position: relative;
    top: 0;
    right: auto;
    width: 75%;
    transform: scale(1);
  }
  @media ${device.laptopM} {
    top: calc((503px / 2) - (768px / 2));
    left: calc(60% - (1050px / 2));
    position: absolute;
    width: 1024px;
    transform: scale(0.9);
  }
  @media ${device.laptopL} {
    position: absolute;
    top: calc((503px / 2) - (768px / 2));
    left: calc(60% - (990px / 2));
    width: 1024px;
    transform: scale(1);
  }
`;

export const VehicleHeaderWrapper = styled.div<VehicleHeaderWrapperProps>`
  width: calc(100% - 55px);
  margin: 0 auto;
  ${(props: { fallback?: boolean | undefined }) => {
    if (props.fallback)
      return css`
        width: calc(100% - 55px);
      `;
    return css``;
  }}
  @media ${device.mobileM} {
    width: calc(100% - 56px);
    margin: 0 auto;
  }
  @media ${device.tablet} {
    width: 100%;
    margin: 0;
  }
  @media ${device.laptopM} {
    width: ${({ isUsed }) => (isUsed ? '100%' : 'calc(100% - 128px)')};
    ${(props: { fallback?: boolean | undefined }) => {
      if (props.fallback)
        return css`
          width: calc(100% - 100px);
        `;
      return css``;
    }}
  }
  @media ${device.laptopL} {
    width: ${({ isUsed }) => (isUsed ? 'calc(100% - 80px)' : 'calc(100% - 192px)')};
    margin: 0 auto;
    ${(props: { fallback?: boolean | undefined }) => {
      if (props.fallback)
        return css`
          width: calc(100% - 200px);
        `;
      return css``;
    }}
  }
  @media ${device.laptopXL} {
    margin: 0 auto;
    width: calc(100% - 192px);
    ${(props: { fallback?: boolean | undefined }) => {
      if (props.fallback)
        return css`
          width: calc(100% - 400px);
        `;
      return css``;
    }}
  }
  @media ${device.desktop} {
    width: calc(100% - 192px);
    max-width: 1248px;
    margin: 0 auto;
  }
`;

export const FlickFusionWrapper = styled.div`
  position: absolute;
  z-index: 2;
  margin-top: ${SPACING_M_L}px;
  @media ${device.tablet} {
    margin-top: ${SPACING_L_M}px;
  }
`;

export const ImpelCTAWrapper = styled.div`
  display: inline-block;
  margin-top: 0px;

  @media ${device.laptopM} {
    margin-top: 28px;
  }
`;
